/* global Office */
/**
 * useSendFlow
 * Implements the DoEncryptMail decision logic (ItemSend pipeline equivalent).
 * Returns { triggerSendFlow } which resolves to a send-flow result object.
 *
 * Decision order:
 *   A) autoEncrypt stored recipients  → DoEncryptMail = true (silent)
 *   B) remindEncrypt stored recipients → DoEncryptMail = true (after confirm)
 *   C) remindEncrypt all recipients    → DoEncryptMail = true (always)
 *
 * If DoEncryptMail == true, the returned result contains prefill data for PasswordModal.
 * The caller decides whether to show the modal based on result.doEncrypt.
 */

import { useCallback } from "react";
import {
  normalizeRecipients,
  fetchAndDecryptAll,
  findByRecipientSetKey,
} from "../services/PasswordManagerService";
import { getAttachmentItems } from "../utils/officeItemHelpers";


/**
 * Collects To + Cc recipients from the active Office.js mail item.
 * Falls back to MOCK_DEV_RECIPIENTS in dev mode.
 *
 * @returns {Promise<string[]>} - Array of email address strings
 */
async function collectOfficeRecipients() {
  if (typeof Office === "undefined" || !Office.context?.mailbox?.item) {
    console.warn("[useSendFlow] Office.js not available – returning empty recipients");
    return [];
  }

  const item = Office.context.mailbox.item;

  const getAsync = (field) =>
    new Promise((resolve) => {
      field.getAsync((result) => {
        resolve(
          result.status === Office.AsyncResultStatus.Succeeded
            ? result.value
            : [],
        );
      });
    });

  const [toList, ccList] = await Promise.all([
    getAsync(item.to),
    getAsync(item.cc),
  ]);

  return [...toList, ...ccList].map((r) => r.emailAddress);
}

/**
 * @param {object} params
 * @param {object|null}  params.settings              - BenutzerSettings from useLicenseContext
 * @param {boolean}      params.isUnlocked             - from CryptoContext
 * @param {boolean}      params.passwortManagerAktiv   - from usePasswordManager().isActive
 * @param {string|null}  params.oid                - from CryptoContext
 * @param {string|null}  params.passwortDatenbankId    - from CryptoContext
 * @param {Uint8Array|null} params.masterKey           - from CryptoContext
 */
export function useSendFlow({
  settings,
  isUnlocked,
  passwortManagerAktiv,
  oid,
  passwortDatenbankId,
  masterKey,
}) {
  /**
   * Runs the full DoEncryptMail decision flow.
   *
   * @returns {Promise<{
   *   aborted: boolean,
   *   doEncrypt: boolean,
   *   recipientSetKey: string,
   *   decryptedEntries: Array|null,
   *   prefillPassword: string|null,
   *   existingEntryId: string|null,
   * }>}
   */
  const triggerSendFlow = useCallback(async () => {
    // ── Step 1: Collect recipients + attachments ──────────────────────────────
    const recipientEmails = await collectOfficeRecipients();
    const recipientSetKey = normalizeRecipients(recipientEmails);

    const allItems = await getAttachmentItems();
    const nonInlineItems = allItems.filter((a) => !a.isInline);
    const hasAttachments = nonInlineItems.length > 0;
    const allAttachmentsPdf =
      hasAttachments &&
      nonInlineItems.every((a) => a.name?.toLowerCase().endsWith(".pdf"));
    const totalAttachmentBytes = nonInlineItems.reduce((sum, a) => sum + (a.size ?? 0), 0);

    // ── Step 2: PM lookup (only if PM active and unlocked) ───────────────────
    let prefillPassword = null;
    let decryptedEntries = null;
    let existingEntryId = null;

    if (passwortManagerAktiv && isUnlocked && oid && passwortDatenbankId && masterKey) {
      try {
        decryptedEntries = await fetchAndDecryptAll(oid, passwortDatenbankId, masterKey);
        const found = findByRecipientSetKey(decryptedEntries, recipientSetKey);
        if (found) {
          prefillPassword = found.password;
          existingEntryId = found.id;
        }
      } catch (err) {
        console.error("[useSendFlow] PM lookup failed:", err);
      }
    }

    // ── Step 3: DoEncryptMail decision ───────────────────────────────────────
    let doEncrypt = false;
    let doEncryptReason = null;

    // Remind to encrypt for all recipients (always)
    if (settings?.erinnerungVerschluesselnAlleEmpfaenger && !doEncrypt) {
      doEncrypt = true;
      doEncryptReason = "remind-all";
    }

    // ── Step 4: Return result ────────────────────────────────────────────────
    return {
      aborted: false,
      doEncrypt,
      recipientSetKey,
      decryptedEntries,
      prefillPassword,
      existingEntryId,
      hasAttachments,
      allAttachmentsPdf,
      totalAttachmentBytes,
    };
  }, [settings, isUnlocked, passwortManagerAktiv, oid, passwortDatenbankId, masterKey]);

  return { triggerSendFlow };
}
